import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, createVNode as _createVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode, withKeys as _withKeys, Transition as _Transition, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, mergeProps as _mergeProps, createTextVNode as _createTextVNode, isRef as _isRef, createSlots as _createSlots } from "vue";
const _hoisted_1 = ["textContent"];
const _hoisted_2 = {
    key: 0,
    class: "rec-select-menu__header"
};
const _hoisted_3 = { class: "rec-select-menu__header__label" };
const _hoisted_4 = {
    key: 0,
    class: "rec-select-menu__header__controls"
};
const _hoisted_5 = {
    key: 1,
    class: "rec-select-menu__content-top"
};
const _hoisted_6 = {
    key: 2,
    class: "rec-select-menu__filters"
};
const _hoisted_7 = { class: "rec-select-menu__options__scroll" };
const _hoisted_8 = ["aria-label", "onClick", "onKeydown", "textContent"];
const _hoisted_9 = {
    key: 5,
    class: "rec-select-menu__tags"
};
const _hoisted_10 = {
    key: 0,
    class: "rec-select__tags"
};
import { mergeComponentClasses, toRem } from "@/common/helpers/style";
import { isArrayItem } from "@/common/helpers/arrays";
import { useVModel } from "@vueuse/core";
import { computed, inject, nextTick, ref, useSlots, useTemplateRef, watch } from "vue";
// Components
import DropdownMenu from "@/common/components/DropdownMenu.vue";
import PageMessage from "@/common/components/PageMessage.vue";
import RecCheckboxes from "@/common/components/RecCheckboxes.vue";
import RecRadioButtons from "@/common/components/RecRadioButtons.vue";
import RecTag from "@/common/components/RecTag.vue";
import SearchInput from "@/common/components/SearchInput.vue";
import SelectAllLinks from "@/common/components/SelectAllLinks.vue";
import SvgIcon from "@/common/components/SvgIcon.vue";
// Setup
export default /*@__PURE__*/ _defineComponent({
    __name: 'RecSelect',
    props: {
        modelValue: {},
        options: { default: () => [] },
        menu: { type: Boolean },
        type: { default: "select" },
        idPrefix: { default: () => `itemselect_${crypto.randomUUID()}_` },
        inputName: { default: undefined },
        placeholder: { default: "" },
        label: { default: undefined },
        hideHeader: { type: Boolean, default: false },
        menuTitle: { default: "" },
        showManage: { type: Boolean },
        showNew: { type: Boolean },
        selectAllText: { default: () => inject("i18n").t("Select All") },
        unselectAllText: { default: () => inject("i18n").t("Deselect All") },
        showSelectionTagArea: { type: Boolean },
        showSelectionMenuTagArea: { type: Boolean },
        showSelectionCountInLabel: { type: Boolean, default: true },
        tagLimit: { default: 0 },
        tooltip: { default: undefined },
        disabled: { type: Boolean },
        menuWidth: { default: 0 },
        filterText: { default: () => inject("i18n").t("Filter") },
        highlightSelected: { type: Boolean, default: true },
        highlightEmpty: { type: Boolean, default: false },
        absolute: { type: Boolean },
        buttonClass: { default: "" },
        buttonPrependIcon: { default: undefined },
        clearable: { type: Boolean },
        ignoreClickOutside: { default: () => [] },
        searchInputAutofocus: { type: Boolean },
        searchKeys: { default: () => [] },
        buttonOnly: { type: Boolean },
        useVShow: { type: Boolean },
        menuPosition: { default: undefined },
        filterSearchOnInput: { type: Boolean },
        menuMaxHeight: { default: undefined },
        menuMinHeight: { default: undefined },
        autoClose: { type: Boolean },
        lockPositionOnOpen: { type: Boolean },
        placement: { default: undefined },
        maxTagRows: { default: 3 },
        minItemsForFilter: { default: 10 },
        getTags: { type: Function, default: undefined },
        hideFilter: { type: Boolean }
    },
    emits: ["new", "clear", "click", "manage", "tagClick", "update:modelValue", "update:menu"],
    setup(__props, { expose: __expose, emit: __emit }) {
        _useCssVars(_ctx => ({
            "23cb3f8d": (scrollbarWidth.value),
            "bd60ac36": (maxHeightCSS.value)
        }));
        /********************************************
         * Select Component
         *
         * A select dropdown component that includes tags
         * and links to manage or add new items.
         *
         *********************************************/
        const props = __props;
        const emit = __emit;
        const slots = useSlots();
        // Models, refs & computed refs
        const model = useVModel(props, "modelValue", emit); // TODO: use defineModel instead
        const isMultiselect = computed(() => Array.isArray(model.value));
        // This simplifies model-lookup-based logic by allowing to refer to model as an array
        const arrayModel = computed(() => (isMultiselect.value ? model.value : model.value ? [model.value] : []));
        const menuModel = useVModel(props, "menu", emit, {
            defaultValue: false,
            passive: true
        }); // TODO: use defineModel instead
        const isTouchDevice = computed(() => window.Ramius.is_mobile || window.Ramius.is_tablet);
        const searchTerm = ref("");
        const optionsMap = computed(() => props.options.reduce((acc, item) => {
            item.options.map((opt) => acc.set(opt.value, opt));
            return acc;
        }, new Map()));
        const tags = computed(() => {
            if (typeof props.getTags === "function") {
                return props.getTags(arrayModel.value);
            }
            return arrayModel.value
                .map((val) => optionsMap.value.get(val))
                .filter((val) => !!val)
                .map((item) => ({ label: item.label ?? "", value: item.value, readonly: item.disabled }))
                .slice(0, props.tagLimit === 0 ? optionsMap.value.size : props.tagLimit);
        });
        const filteredOptGroups = computed(() => {
            const terms = searchTerm.value
                .toLowerCase()
                .split(/\s+/)
                .map((term) => term.trim())
                .filter((term) => term !== "");
            const isVisible = (opt) => {
                if (!terms.length)
                    return true;
                let str = `${opt.label ?? ""}_${opt.subLabel ?? ""}`.toLowerCase();
                if (props.searchKeys.length) {
                    for (const key of props.searchKeys) {
                        const val = opt.value[key];
                        if (val)
                            str += `_${String(val).toLowerCase()}`;
                    }
                }
                return terms.every((term) => str.includes(term));
            };
            return props.options.map((group) => ({
                ...group,
                options: props.useVShow
                    ? group.options.map((opt) => ({
                        ...opt,
                        hidden: !isVisible(opt)
                    }))
                    : group.options.filter((opt) => isVisible(opt))
            }));
        });
        // Label
        const labelEl = ref();
        const computedLabel = computed(() => {
            if (props.label !== undefined)
                return props.label;
            const str = tags.value.map((t) => t.label).join(", ");
            const strWithCount = `${props.placeholder}${arrayModel.value.length > 0 && props.showSelectionCountInLabel ? " (" + arrayModel.value.length + ")" : ""}`;
            // Do not apply any transformation on the label if no placeholder and no label was provided
            if (props.placeholder === "")
                return str;
            // Otherwise, show selected options up to a limit of 25 characters
            return str.length && str.length < 25 ? str : strWithCount;
        });
        const highlightButton = computed(() => (arrayModel.value.length > 0 && props.highlightSelected) || (arrayModel.value.length == 0 && props.highlightEmpty));
        const showFilter = computed(() => (!props.hideFilter && optionsMap.value.size >= props.minItemsForFilter) || !!slots.searchbar);
        // Selection logic
        const onClear = () => {
            if (!model.value)
                return;
            model.value = (isMultiselect.value
                ? model.value.filter((val) => optionsMap.value.get(val)?.disabled)
                : optionsMap.value.get(model.value)?.disabled
                    ? model.value
                    : undefined);
            menuModel.value = false;
            emit("clear");
        };
        const onUnselect = (value) => {
            if (isMultiselect.value && model.value.includes(value)) {
                model.value = model.value.filter((_, i) => i !== model.value.indexOf(value));
            }
            else if (!isMultiselect.value) {
                model.value = undefined;
            }
        };
        const allGroupItemsSelected = (groupIndex) => {
            return filteredOptGroups.value[groupIndex].options.every((opt) => isArrayItem(arrayModel.value, opt.value));
        };
        const selectAll = (groupIndex) => {
            if (props.disabled || !isMultiselect.value)
                return;
            const selectedItemValues = (groupIndex === undefined
                ? filteredOptGroups.value.reduce((acc, group) => [
                    ...acc,
                    ...group.options.filter((opt) => !opt.disabled && !opt.hidden).map((opt) => opt.value)
                ], [])
                : filteredOptGroups.value[groupIndex].options
                    .filter((opt) => !opt.disabled && !opt.hidden)
                    .map((opt) => opt.value)).filter((val) => !isArrayItem(model.value, val));
            model.value = [...model.value, ...selectedItemValues];
        };
        const unselectAll = (groupIndex) => {
            if (props.disabled || !isMultiselect.value)
                return;
            const excluded = groupIndex === undefined
                ? filteredOptGroups.value.reduce((acc, group) => [
                    ...acc,
                    ...group.options.filter((opt) => !opt.disabled && !opt.hidden).map((opt) => opt.value)
                ], [])
                : filteredOptGroups.value[groupIndex].options
                    .filter((opt) => !opt.disabled && !opt.hidden)
                    .map((opt) => opt.value);
            model.value = model.value.filter((val) => !isArrayItem(excluded, val));
        };
        const toggleSelectAll = (groupIndex) => {
            allGroupItemsSelected(groupIndex) ? unselectAll(groupIndex) : selectAll(groupIndex);
        };
        const onKeydownEnter = (e) => {
            e.preventDefault();
            e.stopPropagation();
            menuModel.value = false;
        };
        // Keyboard navigation
        const onKeydownEsc = (e) => onKeydownEnter(e);
        // Get width of browsers scrollbar
        const scrollbarWidth = computed(() => {
            const outer = document.createElement("div");
            outer.style.visibility = "hidden";
            outer.style.overflow = "scroll";
            document.body.appendChild(outer);
            const inner = document.createElement("div");
            outer.appendChild(inner);
            const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
            outer.parentNode?.removeChild(outer);
            return `${scrollbarWidth}px`;
        });
        // Standard logic for "select all" & "unselect all" controls visibility management
        const hasOptions = computed(() => !!filteredOptGroups.value.map(({ options }) => options.filter((opt) => !opt.hidden)).flat().length);
        const isSelectAllVisible = computed(() => !!searchTerm.value.length && hasOptions.value);
        const isUnselectAllVisible = computed(() => {
            const allOptions = filteredOptGroups.value.reduce((acc, group) => [...acc, ...group.options.map((opt) => opt.value)], []);
            return arrayModel.value.some((selection) => isArrayItem(allOptions, selection));
        });
        const isActive = computed(() => isMultiselect.value ? !!model.value.length : model.value !== undefined);
        const classes = computed(() => [
            "rec-select",
            {
                "rec-select--clearable": props.clearable
            }
        ]);
        const btnClasses = computed(() => mergeComponentClasses({
            "rc-highlight": props.type === "select" && highlightButton.value
        }, props.buttonClass));
        watch(model, () => {
            if (!props.autoClose || !menuModel.value)
                return;
            menuModel.value = false;
        });
        const tagsWrapper = ref();
        const tagEls = ref([]);
        const rowCount = ref(0);
        const rowHeights = ref(new Map());
        const calculateRowCount = () => {
            if (!tagEls.value?.length) {
                rowCount.value = 0;
                rowHeights.value = new Map();
                return;
            }
            const positions = new Map();
            tagEls.value.forEach(({ $el: el }) => {
                const rect = el.getBoundingClientRect();
                const top = Math.round(rect.top);
                if (!positions.has(top)) {
                    positions.set(top, rect.height);
                }
            });
            rowCount.value = positions.size;
            rowHeights.value = positions;
        };
        watch([tags, menuModel], ([nextTags, nextMenu]) => {
            if (nextMenu && nextTags.length > 0) {
                nextTick(calculateRowCount);
            }
        });
        const visibleRows = computed(() => Math.min(rowCount.value, props.maxTagRows));
        const maxHeight = computed(() => {
            if (!rowHeights.value.size)
                return 0;
            const heights = Array.from(rowHeights.value.values()).slice(0, visibleRows.value);
            const totalHeight = heights.reduce((sum, h) => sum + h, 0);
            const gaps = Math.max(0, heights.length - 1) * 6; // 6px gap between rows
            const padding = 18 + 8 + 11; // scroll area top + bottom padding AND tags area bottom padding + 1px for border
            return totalHeight + gaps + padding;
        });
        const maxHeightCSS = computed(() => toRem(maxHeight.value));
        // Exposed refs
        const dropdownMenu = useTemplateRef("dropdownMenu");
        __expose({ menuModel, labelEl, ref: dropdownMenu });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(classes.value)
            }, [
                _createVNode(DropdownMenu, {
                    ref_key: "dropdownMenu",
                    ref: dropdownMenu,
                    modelValue: _unref(menuModel),
                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => (_isRef(menuModel) ? (menuModel).value = $event : null)),
                    "button-class": btnClasses.value,
                    "menu-class": `${!_ctx.$slots.menu ? 'rec-select-menu' : ''}`,
                    active: isActive.value,
                    "auto-close": false,
                    type: _ctx.type,
                    disabled: _ctx.disabled,
                    "menu-width": _ctx.menuWidth,
                    "menu-max-height": _ctx.menuMaxHeight,
                    "menu-min-height": _ctx.menuMinHeight,
                    absolute: _ctx.absolute,
                    "button-only": _ctx.buttonOnly,
                    "use-v-show": _ctx.useVShow,
                    "ignore-click-outside": _ctx.ignoreClickOutside,
                    "menu-position": _ctx.menuPosition,
                    tooltip: _ctx.tooltip,
                    "lock-position-on-open": _ctx.lockPositionOnOpen,
                    placement: _ctx.placement,
                    onKeydown: _withKeys(onKeydownEsc, ["esc"]),
                    onClick: _cache[10] || (_cache[10] = ($event) => (_ctx.$emit('click')))
                }, _createSlots({
                    button: _withCtx(() => [
                        _renderSlot(_ctx.$slots, "button", _normalizeProps(_guardReactiveProps({
                            label: computedLabel.value,
                            class: ['rec-select__label', { 'rec-select__label--placeholder': !arrayModel.value.length }]
                        })), () => [
                            (_ctx.buttonPrependIcon)
                                ? (_openBlock(), _createBlock(SvgIcon, {
                                    key: 0,
                                    icon: _ctx.buttonPrependIcon
                                }, null, 8, ["icon"]))
                                : _createCommentVNode("", true),
                            (computedLabel.value)
                                ? (_openBlock(), _createElementBlock("span", {
                                    key: 1,
                                    ref_key: "labelEl",
                                    ref: labelEl,
                                    class: _normalizeClass(['rec-select__label', { 'rec-select__label--placeholder': !arrayModel.value.length }]),
                                    textContent: _toDisplayString(computedLabel.value)
                                }, null, 10, _hoisted_1))
                                : _createCommentVNode("", true),
                            (!_ctx.clearable || !arrayModel.value.length)
                                ? (_openBlock(), _createBlock(SvgIcon, {
                                    key: 2,
                                    icon: "icon-arrow-down",
                                    class: "rec-dropdown__chevron"
                                }))
                                : (_ctx.clearable && !!arrayModel.value.length)
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 3,
                                        class: "rec-select__clear-button",
                                        type: "button",
                                        onClick: _cache[0] || (_cache[0] = _withModifiers(($event) => (_ctx.disabled ? undefined : onClear()), ["stop"]))
                                    }, [
                                        _createVNode(SvgIcon, {
                                            icon: "icon-close-3",
                                            class: "rec-select__clear-button__icon"
                                        })
                                    ]))
                                    : _createCommentVNode("", true)
                        ])
                    ]),
                    _: 2
                }, [
                    (!_ctx.buttonOnly)
                        ? {
                            name: "menu",
                            fn: _withCtx(({ menuHeight }) => [
                                _renderSlot(_ctx.$slots, "menu", _normalizeProps(_guardReactiveProps({ menuHeight, toggleSelectAll: toggleSelectAll })), () => [
                                    _createElementVNode("div", {
                                        class: _normalizeClass(['rec-select-menu__wrap', { 'rec-select-menu__wrap--no-header': _ctx.hideHeader }]),
                                        onClick: _cache[8] || (_cache[8] = _withModifiers(() => { }, ["stop", "prevent"]))
                                    }, [
                                        (!_ctx.hideHeader && (_ctx.menuTitle.length > 0 || _ctx.showNew || _ctx.showManage))
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                                                _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.menuTitle), 1),
                                                (_ctx.$slots['menu-controls'] || _ctx.showManage || _ctx.showNew)
                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                                        _renderSlot(_ctx.$slots, "menu-controls", {}, () => [
                                                            (_ctx.showManage)
                                                                ? (_openBlock(), _createElementBlock("button", {
                                                                    key: 0,
                                                                    class: "btn btn-sm btn-link",
                                                                    type: "button",
                                                                    onClick: _cache[1] || (_cache[1] = ($event) => (_ctx.$emit('manage')))
                                                                }, _toDisplayString(_ctx.$t("Manage")), 1))
                                                                : _createCommentVNode("", true),
                                                            (_ctx.showNew)
                                                                ? (_openBlock(), _createElementBlock("button", {
                                                                    key: 1,
                                                                    class: "btn btn-sm btn-border",
                                                                    type: "button",
                                                                    onClick: _cache[2] || (_cache[2] = ($event) => (_ctx.$emit('new')))
                                                                }, _toDisplayString(_ctx.$t("New")), 1))
                                                                : _createCommentVNode("", true)
                                                        ])
                                                    ]))
                                                    : _createCommentVNode("", true)
                                            ]))
                                            : _createCommentVNode("", true),
                                        (_ctx.$slots['content-top'])
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                                _renderSlot(_ctx.$slots, "content-top", _normalizeProps(_guardReactiveProps({ hasOptions: hasOptions.value })))
                                            ]))
                                            : _createCommentVNode("", true),
                                        (showFilter.value && _unref(menuModel))
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                                _renderSlot(_ctx.$slots, "searchbar", {}, () => [
                                                    _createVNode(SearchInput, {
                                                        modelValue: searchTerm.value,
                                                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((searchTerm).value = $event)),
                                                        "update-on-input": _ctx.filterSearchOnInput,
                                                        class: _normalizeClass({ 'rc-highlight': searchTerm.value.length > 0 }),
                                                        placeholder: _ctx.filterText + '...',
                                                        autofocus: _ctx.searchInputAutofocus && !isTouchDevice.value,
                                                        onKeydown: _cache[4] || (_cache[4] = _withKeys(_withModifiers(() => { }, ["stop", "prevent"]), ["enter"]))
                                                    }, null, 8, ["modelValue", "update-on-input", "class", "placeholder", "autofocus"])
                                                ]),
                                                _createVNode(_Transition, { name: "slide-down" }, {
                                                    default: _withCtx(() => [
                                                        (hasOptions.value && isMultiselect.value)
                                                            ? (_openBlock(), _createBlock(SelectAllLinks, {
                                                                key: 0,
                                                                "select-all-visible": isSelectAllVisible.value,
                                                                "unselect-all-visible": isUnselectAllVisible.value,
                                                                "select-all-text": _ctx.selectAllText,
                                                                "unselect-all-text": _ctx.unselectAllText,
                                                                disabled: _ctx.disabled,
                                                                onSelectAll: selectAll,
                                                                onUnselectAll: unselectAll
                                                            }, null, 8, ["select-all-visible", "unselect-all-visible", "select-all-text", "unselect-all-text", "disabled"]))
                                                            : _createCommentVNode("", true)
                                                    ]),
                                                    _: 1
                                                })
                                            ]))
                                            : _createCommentVNode("", true),
                                        (hasOptions.value)
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 3,
                                                class: "rec-select-menu__options",
                                                onKeydown: _withKeys(onKeydownEnter, ["enter"])
                                            }, [
                                                _createElementVNode("div", _hoisted_7, [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptGroups.value, (group, groupIdx) => {
                                                        return (_openBlock(), _createElementBlock(_Fragment, { key: groupIdx }, [
                                                            (group.options.length > 0 && (group.label || groupIdx))
                                                                ? (_openBlock(), _createElementBlock("button", {
                                                                    key: 0,
                                                                    type: "button",
                                                                    class: "rec-list-item rec-select-menu__group",
                                                                    "aria-label": _ctx.$t('Select All') + ' ' + group.label,
                                                                    onClick: _withModifiers(($event) => (toggleSelectAll(groupIdx)), ["stop", "prevent"]),
                                                                    onKeydown: _withKeys(_withModifiers(($event) => (toggleSelectAll(groupIdx)), ["stop", "prevent"]), ["space"]),
                                                                    textContent: _toDisplayString(group.label)
                                                                }, null, 40, _hoisted_8))
                                                                : _createCommentVNode("", true),
                                                            (_ctx.$slots.item)
                                                                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(group.options, (item, itemIdx) => {
                                                                    return (_openBlock(), _createElementBlock("div", {
                                                                        key: itemIdx,
                                                                        class: "rec-list-item",
                                                                        onClick: _cache[5] || (_cache[5] = _withModifiers(() => { }, ["stop"]))
                                                                    }, [
                                                                        _renderSlot(_ctx.$slots, "item", _mergeProps({ ref_for: true }, {
                                                                            item,
                                                                            index: itemIdx,
                                                                            selected: Array.isArray(_unref(model)) ? _unref(model).includes(item.value) : _unref(model) === item.value,
                                                                            groupIndex: groupIdx,
                                                                            inputName: _ctx.inputName ?? `${_ctx.idPrefix}_${groupIdx}`,
                                                                            disabled: _ctx.disabled
                                                                        }), () => [
                                                                            _createTextVNode(_toDisplayString(item), 1)
                                                                        ])
                                                                    ]));
                                                                }), 128))
                                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                                                    (Array.isArray(_unref(model)))
                                                                        ? (_openBlock(), _createBlock(RecCheckboxes, {
                                                                            key: 0,
                                                                            id: `${_ctx.idPrefix}_${groupIdx}`,
                                                                            modelValue: _unref(model),
                                                                            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => (_isRef(model) ? (model).value = $event : null)),
                                                                            name: _ctx.inputName ?? `${_ctx.idPrefix}_${groupIdx}`,
                                                                            items: group.options,
                                                                            disabled: _ctx.disabled,
                                                                            "button-class": "rec-list-item"
                                                                        }, null, 8, ["id", "modelValue", "name", "items", "disabled"]))
                                                                        : (_openBlock(), _createBlock(RecRadioButtons, {
                                                                            key: 1,
                                                                            id: `${_ctx.idPrefix}_${groupIdx}`,
                                                                            modelValue: _unref(model),
                                                                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => (_isRef(model) ? (model).value = $event : null)),
                                                                            name: _ctx.inputName ?? `${_ctx.idPrefix}_${groupIdx}`,
                                                                            items: group.options,
                                                                            disabled: _ctx.disabled,
                                                                            "button-class": "rec-list-item"
                                                                        }, null, 8, ["id", "modelValue", "name", "items", "disabled"]))
                                                                ], 64))
                                                        ], 64));
                                                    }), 128))
                                                ])
                                            ], 32))
                                            : (_openBlock(), _createBlock(PageMessage, {
                                                key: 4,
                                                label: _ctx.$t('Nothing to display'),
                                                background: "transparent",
                                                icon: "icon-dashed-circle"
                                            }, null, 8, ["label"])),
                                        (_ctx.showSelectionMenuTagArea)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                                _createElementVNode("div", {
                                                    ref_key: "tagsWrapper",
                                                    ref: tagsWrapper,
                                                    class: "rec-select-menu__tags__scroll"
                                                }, [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tags.value, (tag, index) => {
                                                        return (_openBlock(), _createElementBlock("div", {
                                                            key: JSON.stringify(tag.value),
                                                            class: "rec-select-menu__tags__item"
                                                        }, [
                                                            _renderSlot(_ctx.$slots, "menu-tag", _mergeProps({ ref_for: true }, { tag, index, tagEls: tagEls.value }), () => [
                                                                _createVNode(RecTag, {
                                                                    ref_for: true,
                                                                    ref_key: "tagEls",
                                                                    ref: tagEls,
                                                                    text: tag.label,
                                                                    closable: !tag.readonly,
                                                                    color: "var(--p-d-80)",
                                                                    tag: "span",
                                                                    trim: "2",
                                                                    onClick: _withModifiers(($event) => (_ctx.$emit('tagClick', tag.value)), ["stop"]),
                                                                    onClose: ($event) => (onUnselect(tag.value))
                                                                }, null, 8, ["text", "closable", "onClick", "onClose"])
                                                            ])
                                                        ]));
                                                    }), 128))
                                                ], 512)
                                            ]))
                                            : _createCommentVNode("", true)
                                    ], 2)
                                ])
                            ]),
                            key: "0"
                        }
                        : undefined
                ]), 1032, ["modelValue", "button-class", "menu-class", "active", "type", "disabled", "menu-width", "menu-max-height", "menu-min-height", "absolute", "button-only", "use-v-show", "ignore-click-outside", "menu-position", "tooltip", "lock-position-on-open", "placement"]),
                (_ctx.showSelectionTagArea && !!tags.value.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tags.value, (tag, index) => {
                            return _renderSlot(_ctx.$slots, "selection-tag", _mergeProps({
                                key: tag.value,
                                ref_for: true
                            }, { ...tag, index }), () => [
                                _createVNode(RecTag, {
                                    closable: !tag.readonly,
                                    text: tag.label,
                                    color: "var(--p-d-80)",
                                    tag: "span",
                                    onClick: _withModifiers(($event) => (_ctx.$emit('tagClick', tag.value)), ["stop"]),
                                    onClose: ($event) => (onUnselect(tag.value))
                                }, null, 8, ["closable", "text", "onClick", "onClose"])
                            ]);
                        }), 128))
                    ]))
                    : _createCommentVNode("", true)
            ], 2));
        };
    }
});
